import { useState, useEffect } from 'react'
import { FiPlus, FiEdit2, FiTrash2, FiUserX, FiUserCheck, FiMail, FiPhone, FiShield, FiCalendar, FiUsers, FiKey, FiRefreshCw, FiX, FiSearch, FiUser, FiClock, FiMapPin, FiLayers, FiHeart, FiSun, FiCoffee, FiBriefcase, FiMoreHorizontal, FiCheckCircle, FiXCircle, FiAlertCircle, FiUpload, FiLock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import CalendarioSelector from '../components/CalendarioSelector'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { useAuth } from '../contexts/AuthContext'
import ModalResetPasswordAdmin from '../components/v2/ModalResetPasswordAdmin'
import { PageHeader, Btn } from '../ui'
import { formatearFecha } from '../utils/fechas'

const TIPOS_AUSENCIA_ADMIN = [
  { value: 'baja_medica', label: 'Baja Médica', icon: FiHeart, color: 'text-red-600' },
  { value: 'vacaciones', label: 'Vacaciones', icon: FiSun, color: 'text-amber-600' },
  { value: 'permiso_retribuido', label: 'Permiso Retribuido', icon: FiBriefcase, color: 'text-blue-600' },
  { value: 'asuntos_propios', label: 'Asuntos Propios', icon: FiCoffee, color: 'text-green-600' },
  { value: 'ausencia_justificada', label: 'Ausencia Justificada', icon: FiCheckCircle, color: 'text-purple-600' },
  { value: 'otro', label: 'Otro', icon: FiMoreHorizontal, color: 'text-[var(--ink-2)]' }
]

const TIPO_BADGE = {
  vacaciones: 'bg-amber-100 text-amber-700',
  baja_medica: 'bg-red-100 text-red-700',
  baja_laboral: 'bg-red-100 text-red-700',
  permiso_retribuido: 'bg-blue-100 text-blue-700',
  asuntos_propios: 'bg-green-100 text-green-700',
  ausencia_justificada: 'bg-purple-100 text-purple-700',
  dia_libre: 'bg-teal-100 text-teal-700',
  otro: 'bg-[var(--bg-2)] text-[var(--ink-2)]'
}

const TIPO_LABEL = {
  vacaciones: 'Vacaciones', baja_medica: 'Baja Médica', baja_laboral: 'Baja Laboral',
  permiso_retribuido: 'Permiso Retrib.', asuntos_propios: 'Asuntos Propios',
  ausencia_justificada: 'Ausen. Justif.', dia_libre: 'Día Libre', otro: 'Otro'
}

const ESTADO_BADGE = {
  pendiente: 'bg-yellow-100 text-yellow-700',
  aprobada: 'bg-green-100 text-green-700',
  rechazada: 'bg-red-100 text-red-700',
  documentacion_requerida: 'bg-orange-100 text-orange-700'
}

const ESTADO_LABEL = {
  pendiente: 'Pendiente', aprobada: 'Aprobada', rechazada: 'Rechazada',
  documentacion_requerida: 'Doc. solicitada'
}

const GestionEmpleados = () => {
  const { empleado } = useAuth()
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()
  const [empleados, setEmpleados] = useState([])
  const [loading, setLoading] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('activos')

  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null)
  const [ausenciasEmpleado, setAusenciasEmpleado] = useState([])
  const [loadingAusencias, setLoadingAusencias] = useState(false)

  const [modalEmpleado, setModalEmpleado] = useState(false)
  const [editando, setEditando] = useState(null)
  const [grupos, setGrupos] = useState([])
  const [calendarios, setCalendarios] = useState([])

  const [modalAusencia, setModalAusencia] = useState(false)
  const [formAusencia, setFormAusencia] = useState({ tipo: 'baja_medica', fecha_inicio: '', fecha_fin: '', motivo: '' })
  const [documentoAusencia, setDocumentoAusencia] = useState(null)

  const [modalPin, setModalPin] = useState(false)
  const [pinEmpleado, setPinEmpleado] = useState(null)
  const [pinNuevo, setPinNuevo] = useState('')
  const [guardandoPin, setGuardandoPin] = useState(false)

  // Reset administrativo de contraseña (admin de empresa)
  const [modalResetPassword, setModalResetPassword] = useState(null) // empleado o null

  // Punto 1 — festivos aplicables al empleado (heredados del centro de su grupo)
  const [modalFestivos, setModalFestivos] = useState(null) // { centroId, centroNombre } o null
  const [festivosAplicables, setFestivosAplicables] = useState([])
  const [loadingFestivos, setLoadingFestivos] = useState(false)
  const [anioFestivos, setAnioFestivos] = useState(new Date().getFullYear())

  const [formData, setFormData] = useState({
    nombre: '', email: '', password: '', passwordConfirm: '', pin: '', dni: '', telefono: '',
    is_admin: false, fecha_inicio: '', fecha_fin: '', grupo_id: '', calendario_id: ''
  })

  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const sep = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${sep}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  const token = () => localStorage.getItem('token')
  const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' })

  useEffect(() => {
    cargarEmpleados()
    cargarGruposYCalendarios()
  }, [empresaGestionId])

  const cargarEmpleados = async () => {
    setLoading(true)
    try {
      const r = await fetch(buildApiUrl('/empleados'), { headers: headers() })
      if (r.ok) { const d = await r.json(); setEmpleados(d.empleados || []) }
    } catch { toast.error('Error al cargar empleados') }
    finally { setLoading(false) }
  }

  const cargarGruposYCalendarios = async () => {
    try {
      const [rG, rC] = await Promise.all([
        fetch(buildApiUrl('/grupos-empleados'), { headers: headers() }),
        fetch(buildApiUrl('/calendarios-laborales'), { headers: headers() })
      ])
      if (rG.ok) setGrupos(await rG.json())
      if (rC.ok) setCalendarios(await rC.json())
    } catch {}
  }

  const cargarAusencias = async (empId) => {
    setLoadingAusencias(true)
    try {
      const anio = new Date().getFullYear()
      const r = await fetch(buildApiUrl(`/ausencias/todas?empleado_id=${empId}&anio=${anio}`), { headers: headers() })
      if (r.ok) setAusenciasEmpleado(await r.json())
    } catch {}
    finally { setLoadingAusencias(false) }
  }

  const seleccionarEmpleado = (emp) => {
    setEmpleadoSeleccionado(emp)
    cargarAusencias(emp.id)
  }

  const generarPinAleatorio = async () => {
    try {
      const r = await fetch(buildApiUrl('/empleados/generar-pin'), { headers: headers() })
      if (r.ok) { const d = await r.json(); return d.pin }
      return null
    } catch { return null }
  }

  // Filtrado
  const empleadosFiltrados = empleados.filter(e => {
    if (filtroEstado === 'activos' && !e.activo) return false
    if (filtroEstado === 'inactivos' && e.activo) return false
    if (busqueda) {
      const q = busqueda.toLowerCase()
      return e.nombre?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q) || e.dni?.toLowerCase().includes(q)
    }
    return true
  })

  // Modal empleado
  const abrirModalCrear = async () => {
    try {
      const r = await fetch(buildApiUrl('/empleados/check-slots'), { headers: headers() })
      if (!r.ok) {
        const data = await r.json()
        toast.error(data.error || 'No hay plazas disponibles')
        return
      }
    } catch (error) {
      toast.error('Error al verificar plazas disponibles')
      return
    }
    setEditando(null)
    setFormData({ nombre: '', email: '', password: '', passwordConfirm: '', pin: '', dni: '', telefono: '', is_admin: false, fecha_inicio: '', fecha_fin: '', grupo_id: '', calendario_id: '' })
    setModalEmpleado(true)
  }

  const abrirModalEditar = (emp) => {
    setEditando(emp)
    setFormData({
      nombre: emp.nombre, email: emp.email, password: '', passwordConfirm: '', pin: '',
      dni: emp.dni || '', telefono: emp.telefono || '', is_admin: emp.is_admin === 1,
      fecha_inicio: emp.fecha_inicio || '', fecha_fin: emp.fecha_fin || '',
      grupo_id: emp.grupo_id || '', calendario_id: emp.calendario_id || ''
    })
    setModalEmpleado(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (loading) return // evita doble submit por doble click
    if (!formData.nombre || !formData.email) { toast.error('Nombre y email son obligatorios'); return }
    if (!editando && !formData.password) { toast.error('La contraseña es obligatoria'); return }
    if (!editando && formData.password.length < 6) { toast.error('La contraseña debe tener mínimo 6 caracteres'); return }
    if (!editando && formData.password !== formData.passwordConfirm) { toast.error('Las contraseñas no coinciden'); return }
    if (formData.pin && !/^\d{5,8}$/.test(formData.pin)) { toast.error('PIN: 5-8 dígitos'); return }
    // Validación calendario obligatorio: tanto al crear como al editar (salvo admins).
    // Si tiene grupo, comprobamos también que el grupo tenga calendario asociado.
    if (!formData.is_admin) {
      const grupoSeleccionado = formData.grupo_id ? grupos.find(g => g.id === parseInt(formData.grupo_id)) : null
      const grupoTieneCal = grupoSeleccionado && grupoSeleccionado.calendario_id
      if (!formData.calendario_id && !grupoTieneCal) {
        toast.error('Asigna un calendario personal o un grupo con calendario. Sin calendario el empleado no podrá fichar.', { duration: 8000 })
        return
      }
    }

    setLoading(true)
    try {
      const url = editando ? buildApiUrl(`/empleados/${editando.id}`) : buildApiUrl('/empleados')
      const body = editando
        ? { nombre: formData.nombre, email: formData.email, dni: formData.dni, telefono: formData.telefono, is_admin: formData.is_admin, fecha_inicio: formData.fecha_inicio || null, fecha_fin: formData.fecha_fin || null, grupo_id: formData.grupo_id ? parseInt(formData.grupo_id) : null, calendario_id: formData.calendario_id ? parseInt(formData.calendario_id) : null }
        : { ...formData, grupo_id: formData.grupo_id ? parseInt(formData.grupo_id) : null, calendario_id: formData.calendario_id ? parseInt(formData.calendario_id) : null, pin: formData.pin || undefined }

      const r = await fetch(url, { method: editando ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(body) })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success(editando ? 'Empleado actualizado' : 'Empleado creado')
      setModalEmpleado(false)
      cargarEmpleados()
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const desactivarEmpleado = async (id) => {
    if (!window.confirm('¿Desactivar este empleado? No podrá acceder a la aplicación pero se mantendrán sus datos y registros.')) return
    try {
      const r = await fetch(buildApiUrl(`/empleados/${id}`), { method: 'DELETE', headers: headers() })
      if (!r.ok) { const d = await r.json(); throw new Error(d.error) }
      toast.success('Empleado desactivado')
      cargarEmpleados()
      if (empleadoSeleccionado?.id === id) setEmpleadoSeleccionado(null)
    } catch (error) { toast.error(error.message) }
  }

  const reactivarEmpleado = async (emp) => {
    if (!window.confirm(`¿Reactivar a ${emp.nombre}? Ocupará una plaza de empleado.`)) return
    try {
      const r = await fetch(buildApiUrl(`/empleados/${emp.id}`), {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ activo: true })
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error al reactivar')
      toast.success(`${emp.nombre} reactivado`)
      cargarEmpleados()
      setEmpleadoSeleccionado({ ...emp, activo: 1 })
    } catch (error) { toast.error(error.message) }
  }

  // PIN
  const abrirModalPin = async (emp) => {
    setPinEmpleado(emp)
    setPinNuevo('')
    setModalPin(true)
    const pin = await generarPinAleatorio()
    if (pin) setPinNuevo(pin)
  }

  const handleAsignarPin = async () => {
    if (!pinNuevo || !/^\d{5,8}$/.test(pinNuevo)) { toast.error('PIN: 5-8 dígitos'); return }
    setGuardandoPin(true)
    try {
      const url = esGestion && empresaGestionId
        ? `${API_URL}/auth/empleado/${pinEmpleado.id}/pin?empresa_id=${empresaGestionId}`
        : `${API_URL}/auth/empleado/${pinEmpleado.id}/pin`
      const r = await fetch(url, { method: 'PUT', headers: headers(), body: JSON.stringify({ pin: pinNuevo }) })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error)
      toast.success('PIN actualizado')
      setModalPin(false)
      cargarEmpleados()
    } catch (error) { toast.error(error.message) }
    finally { setGuardandoPin(false) }
  }

  // Ausencia admin
  const abrirModalAusencia = () => {
    setFormAusencia({ tipo: 'baja_medica', fecha_inicio: '', fecha_fin: '', motivo: '' })
    setDocumentoAusencia(null)
    setModalAusencia(true)
  }

  const crearAusenciaAdmin = async (e) => {
    e.preventDefault()
    if (loading) return // evita doble submit
    if (!formAusencia.fecha_inicio || !formAusencia.fecha_fin) { toast.error('Fechas obligatorias'); return }
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append('empleado_id', empleadoSeleccionado.id)
      fd.append('tipo', formAusencia.tipo)
      fd.append('fecha_inicio', formAusencia.fecha_inicio)
      fd.append('fecha_fin', formAusencia.fecha_fin)
      if (formAusencia.motivo) fd.append('motivo', formAusencia.motivo)
      if (documentoAusencia) fd.append('documento', documentoAusencia)

      const r = await fetch(buildApiUrl('/ausencias/admin'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token()}` },
        body: fd
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success('Ausencia registrada')
      setModalAusencia(false)
      cargarAusencias(empleadoSeleccionado.id)
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const eliminarAusenciaAdmin = async (ausenciaId) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return
    try {
      const r = await fetch(buildApiUrl(`/ausencias/admin/${ausenciaId}`), { method: 'DELETE', headers: headers() })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success('Ausencia eliminada')
      cargarAusencias(empleadoSeleccionado.id)
    } catch (error) { toast.error(error.message) }
  }

  const [modalSolicitarDoc, setModalSolicitarDoc] = useState(null) // ausencia id
  const [mensajeDoc, setMensajeDoc] = useState('')

  const solicitarDocumentacion = async () => {
    try {
      const r = await fetch(buildApiUrl(`/ausencias/${modalSolicitarDoc}/solicitar-documentacion`), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ mensaje: mensajeDoc || 'Se requiere documentación justificativa' })
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success('Documentación solicitada al empleado')
      setModalSolicitarDoc(null)
      cargarAusencias(empleadoSeleccionado.id)
    } catch (error) { toast.error(error.message) }
  }

  const grupoEmpleado = (emp) => grupos.find(g => g.id === emp.grupo_id)

  // Punto 1 — abre el modal con los festivos del centro heredado del grupo.
  const verFestivosAplicables = async (centroId, centroNombre) => {
    setModalFestivos({ centroId, centroNombre })
    setFestivosAplicables([])
    setLoadingFestivos(true)
    try {
      const r = await fetch(buildApiUrl(`/centros-trabajo/${centroId}/festivos?anio=${anioFestivos}`), { headers: headers() })
      if (r.ok) setFestivosAplicables(await r.json())
      else toast.error('No se pudieron cargar los festivos')
    } catch { toast.error('Error al cargar festivos') }
    finally { setLoadingFestivos(false) }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ E1"
        kicker="Empleados"
        title="Plantilla de la"
        italic="empresa."
        sub="Alta, edición y gestión de empleados, grupos y ausencias."
        actions={
          <Btn variant="primary" size="sm" onClick={abrirModalCrear}>
            <FiPlus size={14} /> Nuevo empleado
          </Btn>
        }
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de empleados */}
        <div className="lg:col-span-1">
          <div className="ui-card p-4">
            {/* Búsqueda y filtro */}
            <div className="mb-3 space-y-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)] pointer-events-none" size={16} />
                <input value={busqueda} onChange={e => setBusqueda(e.target.value)} className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full pl-10" placeholder="Buscar empleado..." />
              </div>
              <div className="flex gap-1">
                {['activos', 'inactivos', 'todos'].map(f => (
                  <button key={f} onClick={() => setFiltroEstado(f)}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${filtroEstado === f ? 'bg-blue-100 text-blue-700' : 'text-[var(--ink-3)] hover:bg-[var(--bg-2)]'}`}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <h2 className="font-semibold text-[var(--ink-2)] mb-3 text-sm">Empleados ({empleadosFiltrados.length})</h2>

            {loading ? (
              <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
            ) : empleadosFiltrados.length === 0 ? (
              <p className="text-[var(--ink-4)] text-sm text-center py-8">No hay empleados</p>
            ) : (
              <div className="space-y-1.5 max-h-[65vh] overflow-y-auto">
                {empleadosFiltrados.map(emp => (
                  <div key={emp.id} onClick={() => seleccionarEmpleado(emp)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${empleadoSeleccionado?.id === emp.id ? 'border-blue-500 bg-blue-50' : 'border-[var(--line)] hover:border-[var(--line-2)] hover:bg-[var(--bg-2)]'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[var(--ink)] text-sm truncate">{emp.nombre}</p>
                          {emp.is_admin === 1 && <span className="flex-shrink-0 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Admin</span>}
                        </div>
                        <p className="text-xs text-[var(--ink-3)] truncate">{emp.email}</p>
                        {emp.grupo_id && (
                          <p className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
                            <FiLayers size={10} /> {grupos.find(g => g.id === emp.grupo_id)?.nombre || 'Grupo'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <span className={`w-2 h-2 rounded-full ${emp.activo ? 'bg-green-500' : 'bg-red-400'}`} />
                        <button onClick={e => { e.stopPropagation(); abrirModalPin(emp) }}
                          className={`p-1 rounded text-xs ${emp.has_pin ? 'text-green-600' : 'text-[var(--ink-4)]'}`} title="PIN">
                          <FiKey size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Panel de detalle */}
        <div className="lg:col-span-2">
          {!empleadoSeleccionado ? (
            <div className="ui-card p-8 flex flex-col items-center justify-center text-[var(--ink-4)] h-64">
              <FiUser size={40} className="mb-3 opacity-30" />
              <p>Selecciona un empleado para ver su información</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info del empleado */}
              <div className="ui-card p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-[var(--ink)]">{empleadoSeleccionado.nombre}</h2>
                      {empleadoSeleccionado.is_admin === 1 && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Admin</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${empleadoSeleccionado.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {empleadoSeleccionado.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => abrirModalEditar(empleadoSeleccionado)} className="btn btn-secondary text-sm flex items-center gap-1">
                      <FiEdit2 size={14} /> Editar
                    </button>
                    <button
                      onClick={() => setModalResetPassword(empleadoSeleccionado)}
                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                      title="Resetear contraseña"
                    >
                      <FiLock size={16} />
                    </button>
                    {empleadoSeleccionado.activo ? (
                      <button onClick={() => desactivarEmpleado(empleadoSeleccionado.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Desactivar empleado">
                        <FiUserX size={16} />
                      </button>
                    ) : (
                      <button onClick={() => reactivarEmpleado(empleadoSeleccionado)} className="btn btn-success text-sm flex items-center gap-1" title="Reactivar empleado">
                        <FiUserCheck size={14} /> Reactivar
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <FiMail className="text-[var(--ink-4)]" size={14} />
                    <span className="text-[var(--ink-2)] truncate">{empleadoSeleccionado.email}</span>
                  </div>
                  {empleadoSeleccionado.telefono && (
                    <div className="flex items-center gap-2 text-sm">
                      <FiPhone className="text-[var(--ink-4)]" size={14} />
                      <span className="text-[var(--ink-2)]">{empleadoSeleccionado.telefono}</span>
                    </div>
                  )}
                  {empleadoSeleccionado.dni && (
                    <div className="flex items-center gap-2 text-sm">
                      <FiUser className="text-[var(--ink-4)]" size={14} />
                      <span className="text-[var(--ink-2)]">{empleadoSeleccionado.dni}</span>
                    </div>
                  )}
                  {empleadoSeleccionado.grupo_id && (
                    <div className="flex items-center gap-2 text-sm">
                      <FiLayers className="text-blue-500" size={14} />
                      <span className="text-blue-700">{grupoEmpleado(empleadoSeleccionado)?.nombre || '—'}</span>
                    </div>
                  )}
                  {/* Punto 1 — centro de trabajo heredado del grupo (read-only) + festivos */}
                  {(() => {
                    const g = grupoEmpleado(empleadoSeleccionado)
                    const centroId = g?.centro_trabajo_id
                    const centroNombre = g?.centro_trabajo_nombre
                    if (!empleadoSeleccionado.grupo_id) {
                      return (
                        <div className="flex items-center gap-2 text-sm">
                          <FiMapPin className="text-[var(--ink-4)]" size={14} />
                          <span className="text-amber-600">Sin grupo · sin festivos asignados</span>
                        </div>
                      )
                    }
                    if (!centroId) {
                      return (
                        <div className="flex items-center gap-2 text-sm">
                          <FiMapPin className="text-[var(--ink-4)]" size={14} />
                          <span className="text-amber-600">Grupo sin centro · sin festivos</span>
                        </div>
                      )
                    }
                    return (
                      <div className="flex items-center gap-2 text-sm">
                        <FiMapPin className="text-green-600" size={14} />
                        <span className="text-[var(--ink-2)] truncate" title={centroNombre}>{centroNombre}</span>
                        <button
                          type="button"
                          onClick={() => verFestivosAplicables(centroId, centroNombre)}
                          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                        >
                          Ver festivos
                        </button>
                      </div>
                    )
                  })()}
                  {empleadoSeleccionado.has_pin ? (
                    <div className="flex items-center gap-2 text-sm">
                      <FiKey className="text-green-500" size={14} />
                      <span className="text-green-700">PIN asignado</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <FiKey className="text-[var(--ink-4)]" size={14} />
                      <span className="text-[var(--ink-4)]">Sin PIN</span>
                    </div>
                  )}
                  {(empleadoSeleccionado.fecha_inicio || empleadoSeleccionado.fecha_fin) && (
                    <div className="flex items-center gap-2 text-sm">
                      <FiCalendar className="text-[var(--ink-4)]" size={14} />
                      <span className="text-[var(--ink-2)]">
                        {empleadoSeleccionado.fecha_inicio && formatearFecha(empleadoSeleccionado.fecha_inicio)}
                        {empleadoSeleccionado.fecha_inicio && empleadoSeleccionado.fecha_fin && ' → '}
                        {empleadoSeleccionado.fecha_fin && formatearFecha(empleadoSeleccionado.fecha_fin)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Banner empleado inactivo */}
              {!empleadoSeleccionado.activo && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
                  <FiAlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">Empleado desactivado</p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      No puede acceder a la aplicación. Sus registros y reportes se mantienen.
                      {empleadoSeleccionado.fecha_fin && new Date(empleadoSeleccionado.fecha_fin) < new Date() && (
                        <span> Fecha fin de contrato: {formatearFecha(empleadoSeleccionado.fecha_fin)}.</span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Ausencias del empleado */}
              <div className="ui-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[var(--ink)] flex items-center gap-2">
                    <FiCalendar size={16} /> Ausencias {new Date().getFullYear()}
                  </h3>
                  <button onClick={abrirModalAusencia} className="btn btn-secondary text-sm flex items-center gap-1">
                    <FiPlus size={14} /> Registrar ausencia
                  </button>
                </div>

                {loadingAusencias ? (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-10 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
                ) : ausenciasEmpleado.length === 0 ? (
                  <div className="text-center py-6 text-[var(--ink-4)]">
                    <FiCalendar size={28} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Sin ausencias registradas este año</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ausenciasEmpleado.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-[var(--line)] hover:bg-[var(--bg-2)]">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIPO_BADGE[a.tipo] || TIPO_BADGE.otro}`}>
                            {TIPO_LABEL[a.tipo] || a.tipo}
                          </span>
                          <span className="text-sm text-[var(--ink-2)] font-mono">
                            {formatearFecha(a.fecha_inicio)}
                            {a.fecha_inicio !== a.fecha_fin && ` → ${formatearFecha(a.fecha_fin)}`}
                          </span>
                          <span className="text-xs text-[var(--ink-3)]">{a.dias_laborables}d</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ESTADO_BADGE[a.estado] || ''}`}>
                            {a.estado === 'pendiente' && <FiClock className="inline mr-1" size={10} />}
                            {a.estado === 'aprobada' && <FiCheckCircle className="inline mr-1" size={10} />}
                            {a.estado === 'rechazada' && <FiXCircle className="inline mr-1" size={10} />}
                            {a.estado === 'documentacion_requerida' && <FiAlertCircle className="inline mr-1" size={10} />}
                            {ESTADO_LABEL[a.estado] || a.estado}
                          </span>
                          {a.documento_justificativo ? (
                            <a href={`${API_URL}/uploads/ausencias/${a.documento_justificativo}`} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-green-600 hover:text-green-800 flex items-center gap-0.5" title="Ver documento adjunto">
                              📎 <span className="underline">Ver</span>
                            </a>
                          ) : a.tipo_requiere_documento ? (
                            <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium" title="Justificante pendiente">
                              Sin justificante
                            </span>
                          ) : null}
                          {(a.estado === 'pendiente' || a.estado === 'aprobada') && !a.documento_justificativo && (
                            <button onClick={() => { setModalSolicitarDoc(a.id); setMensajeDoc('') }}
                              className="p-1 text-orange-400 hover:text-orange-600 rounded" title="Solicitar documentación">
                              <FiUpload size={13} />
                            </button>
                          )}
                          <button onClick={() => eliminarAusenciaAdmin(a.id)} className="p-1 text-[var(--ink-4)] hover:text-red-500 rounded" title="Eliminar ausencia">
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear/editar empleado */}
      {modalEmpleado && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setModalEmpleado(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--line)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FiUser className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-base font-bold text-[var(--ink)]">{editando ? 'Editar empleado' : 'Nuevo empleado'}</h3>
              </div>
              <button onClick={() => setModalEmpleado(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-[var(--ink-4)] hover:text-[var(--ink-2)] hover:bg-[var(--bg-2)]"><FiX size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5">
              {/* Datos personales */}
              <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-3">Datos personales</p>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Nombre completo *</label>
                  <input required value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="Nombre y apellidos" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Email *</label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="usuario@empresa.com" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">DNI/NIF</label>
                  <input value={formData.dni} onChange={e => setFormData({...formData, dni: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="12345678A" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Teléfono</label>
                  <input type="tel" value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="+34 600 000 000" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Fecha inicio</label>
                  <input type="date" value={formData.fecha_inicio} onChange={e => setFormData({...formData, fecha_inicio: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Fecha fin</label>
                  <input type="date" value={formData.fecha_fin} onChange={e => setFormData({...formData, fecha_fin: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                </div>
              </div>

              {/* Credenciales + Organización lado a lado */}
              <div className="grid grid-cols-2 gap-6 mb-5">
                {/* Columna izquierda: Credenciales (crear) u Organización (editar) */}
                {!editando ? (
                  <div>
                    <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-3">Credenciales</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Contraseña *</label>
                        <input type="password" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="Mínimo 6 caracteres" minLength={6} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Confirmar contraseña *</label>
                        <input type="password" required value={formData.passwordConfirm} onChange={e => setFormData({...formData, passwordConfirm: e.target.value})}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 bg-white ${formData.passwordConfirm && formData.password !== formData.passwordConfirm ? 'border-red-300 focus:ring-red-300' : 'border-[var(--line-2)] focus:ring-blue-500 focus:border-blue-500'}`}
                          placeholder="Repite la contraseña" minLength={6} />
                        {formData.passwordConfirm && formData.password !== formData.passwordConfirm && (
                          <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">PIN de fichaje</label>
                        <div className="flex gap-2">
                          <input type="text" inputMode="numeric" pattern="\d*" maxLength={8}
                            value={formData.pin} onChange={e => setFormData({...formData, pin: e.target.value.replace(/\D/g, '')})}
                            className="flex-1 px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="5-8 dígitos (opcional)" />
                          <button type="button" onClick={async () => { const p = await generarPinAleatorio(); if (p) setFormData({...formData, pin: p}) }}
                            className="px-3 py-2 text-sm font-medium text-[var(--ink-2)] border border-[var(--line-2)] rounded-lg hover:bg-[var(--bg-2)] transition-colors flex items-center gap-1.5" title="Generar PIN aleatorio">
                            <FiRefreshCw size={14} /> Generar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-3">Organización</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Grupo</label>
                        <select value={formData.grupo_id} onChange={e => setFormData({...formData, grupo_id: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                          <option value="">— Sin grupo —</option>
                          {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}{g.centro_trabajo_nombre ? ` (${g.centro_trabajo_nombre})` : ''}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Calendario personal</label>
                        <select value={formData.calendario_id} onChange={e => setFormData({...formData, calendario_id: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                          <option value="">— Usar calendario del grupo —</option>
                          {calendarios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Columna derecha: Organización (crear) o vacía (editar) */}
                <div>
                  <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-3">{editando ? 'Rol' : 'Organización'}</p>
                  <div className="space-y-3">
                    {!editando && (
                      <>
                        <div>
                          <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Grupo</label>
                          <select value={formData.grupo_id} onChange={e => setFormData({...formData, grupo_id: e.target.value})}
                            className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                            <option value="">— Sin grupo —</option>
                            {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}{g.centro_trabajo_nombre ? ` (${g.centro_trabajo_nombre})` : ''}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Calendario personal</label>
                          <select value={formData.calendario_id} onChange={e => setFormData({...formData, calendario_id: e.target.value})}
                            className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                            <option value="">— Usar calendario del grupo —</option>
                            {calendarios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                          </select>
                        </div>
                      </>
                    )}
                    <label className="flex items-center gap-3 p-3 border border-[var(--line-2)] rounded-lg cursor-pointer hover:bg-purple-50 transition-colors">
                      <input type="checkbox" checked={formData.is_admin} onChange={e => setFormData({...formData, is_admin: e.target.checked})}
                        className="w-4 h-4 text-purple-600 rounded" />
                      <div>
                        <span className="text-sm font-medium text-[var(--ink)]">Administrador</span>
                        <p className="text-xs text-[var(--ink-3)]">Gestiona empleados y configuración</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-[var(--line)]">
                <button type="button" onClick={() => setModalEmpleado(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={loading} className="btn btn-primary btn-ripple flex-1 disabled:opacity-60">{loading ? 'Guardando...' : (editando ? 'Guardar cambios' : 'Crear empleado')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal PIN */}
      {modalPin && pinEmpleado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2"><FiKey className="text-blue-600" /> {pinEmpleado.has_pin ? 'Cambiar PIN' : 'Asignar PIN'}</h3>
              <button onClick={() => setModalPin(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[var(--ink-2)]">{pinEmpleado.has_pin ? 'Cambiar' : 'Asignar'} PIN a <strong>{pinEmpleado.nombre}</strong></p>
              <div className="flex gap-2">
                <input type="text" inputMode="numeric" pattern="\d*" maxLength={8}
                  value={pinNuevo} onChange={e => setPinNuevo(e.target.value.replace(/\D/g, ''))}
                  className="input flex-1 text-center text-lg tracking-widest font-mono" placeholder="•••••" />
                <button type="button" onClick={async () => { const p = await generarPinAleatorio(); if (p) setPinNuevo(p) }}
                  className="btn btn-secondary"><FiRefreshCw size={16} /></button>
              </div>
              <p className="text-xs text-[var(--ink-3)]">5 a 8 dígitos</p>
              <div className="flex gap-3">
                <button onClick={() => setModalPin(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button onClick={handleAsignarPin} disabled={guardandoPin || !pinNuevo} className="btn btn-primary flex-1">
                  {guardandoPin ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal reset password (admin / super admin) */}
      {modalResetPassword && (
        <ModalResetPasswordAdmin
          empleado={modalResetPassword}
          onClose={() => setModalResetPassword(null)}
        />
      )}

      {/* Modal registrar ausencia (admin) */}
      {modalAusencia && empleadoSeleccionado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-semibold">Registrar ausencia</h3>
                <p className="text-xs text-[var(--ink-3)] mt-0.5">Para {empleadoSeleccionado.nombre}</p>
              </div>
              <button onClick={() => setModalAusencia(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <form onSubmit={crearAusenciaAdmin} className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[var(--ink-2)] mb-2">Tipo de ausencia</label>
                <div className="grid grid-cols-2 gap-2">
                  {TIPOS_AUSENCIA_ADMIN.map(t => {
                    const Icon = t.icon
                    const isActive = formAusencia.tipo === t.value
                    return (
                      <button key={t.value} type="button" onClick={() => setFormAusencia({...formAusencia, tipo: t.value})}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border text-left text-sm font-medium transition-all ${
                          isActive ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-400' : 'border-[var(--line)] hover:border-[var(--line-2)]'
                        }`}>
                        <Icon size={15} className={isActive ? t.color : 'text-[var(--ink-4)]'} />
                        <span className={isActive ? 'text-[var(--ink)]' : 'text-[var(--ink-2)]'}>{t.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--ink-2)] mb-2">Fechas</label>
                <div className="border border-[var(--line)] rounded-lg p-3">
                  <CalendarioSelector
                    fechaInicio={formAusencia.fecha_inicio}
                    fechaFin={formAusencia.fecha_fin}
                    onSeleccion={(inicio, fin) => setFormAusencia({...formAusencia, fecha_inicio: inicio, fecha_fin: fin})}
                    ausenciasExistentes={ausenciasEmpleado.filter(a => a.estado !== 'rechazada')}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Motivo / Observaciones</label>
                <textarea value={formAusencia.motivo} onChange={e => setFormAusencia({...formAusencia, motivo: e.target.value})}
                  rows={2} className="w-full px-3 py-2 border border-[var(--line)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none" placeholder="Opcional..." />
              </div>
              {/* Documento adjunto */}
              <div>
                <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Documento adjunto</label>
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[var(--line-2)] rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <FiUpload className="text-[var(--ink-4)]" size={18} />
                  <span className="text-sm text-[var(--ink-3)]">
                    {documentoAusencia ? documentoAusencia.name : 'Subir archivo (PDF, JPG, PNG)'}
                  </span>
                  <input type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setDocumentoAusencia(e.target.files[0] || null)} />
                </label>
                {documentoAusencia && (
                  <button type="button" onClick={() => setDocumentoAusencia(null)} className="text-xs text-red-500 hover:text-red-700 mt-1">Quitar archivo</button>
                )}
              </div>
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                <FiAlertCircle className="text-blue-500 mt-0.5 flex-shrink-0" size={14} />
                <p className="text-xs text-blue-700">La ausencia se registrará como aprobada directamente.</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setModalAusencia(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button type="submit" disabled={!formAusencia.fecha_inicio || loading} className="btn btn-primary flex-1 disabled:opacity-50">{loading ? 'Registrando...' : 'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal solicitar documentación */}
      {modalSolicitarDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">Solicitar documentación</h3>
              <button onClick={() => setModalSolicitarDoc(null)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[var(--ink-2)]">Se notificará al empleado que debe adjuntar documentación a esta ausencia.</p>
              <div>
                <label className="label">Mensaje para el empleado</label>
                <textarea value={mensajeDoc} onChange={e => setMensajeDoc(e.target.value)} rows={3}
                  className="input w-full resize-none" placeholder="Ej: Por favor adjunta el parte de baja médica..." />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setModalSolicitarDoc(null)} className="btn btn-secondary flex-1">Cancelar</button>
                <button onClick={solicitarDocumentacion} className="btn btn-primary flex-1">Solicitar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Punto 1 — Modal festivos aplicables (heredados del centro del grupo) */}
      {modalFestivos && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-semibold">Festivos aplicables</h3>
                <p className="text-xs text-[var(--ink-3)] mt-0.5 flex items-center gap-1">
                  <FiMapPin size={11} /> {modalFestivos.centroNombre}
                </p>
              </div>
              <button onClick={() => setModalFestivos(null)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="flex items-center gap-2">
                <label className="text-sm text-[var(--ink-3)]">Año</label>
                <select
                  value={anioFestivos}
                  onChange={e => { const y = Number(e.target.value); setAnioFestivos(y); verFestivosAplicables(modalFestivos.centroId, modalFestivos.centroNombre) }}
                  className="input text-sm py-1 px-2"
                >
                  {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {loadingFestivos ? (
                <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-9 bg-[var(--bg-2)] rounded animate-pulse" />)}</div>
              ) : festivosAplicables.length === 0 ? (
                <div className="text-center py-8 text-[var(--ink-4)]">
                  <FiCalendar size={28} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay festivos para {anioFestivos} en este centro</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[var(--ink-3)] border-b">
                    <th className="pb-2 font-medium">Fecha</th>
                    <th className="pb-2 font-medium">Nombre</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {festivosAplicables.map(f => (
                      <tr key={f.id}>
                        <td className="py-2 font-mono text-[var(--ink-2)]">{formatearFecha(f.fecha)}</td>
                        <td className="py-2 text-[var(--ink)]">{f.nombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t">
              <button onClick={() => setModalFestivos(null)} className="btn btn-secondary w-full">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionEmpleados
